import type { CityConfig } from "../hawaii-city/types";

export const mililaniConfig: CityConfig = {
  city: "Mililani",
  state: "Hawaii",
  stateAbbr: "HI",
  slug: "mililani",
  route: "/mililani",
  title: "Emergency & Expedited Passport Services in Mililani, HI — Central Oahu | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Mililani, HI. Fast passport renewal and urgent passport help for Central Oahu, Wahiawa, and Waipio travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Mililani, HI",
  heroSubtitle: "Mililani, Wahiawa, and Central Oahu residents — get your passport fast without driving through Honolulu traffic. Start online, ship overnight, and we handle the rest. As fast as 24 hours.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Beautiful%20suburban%20Mililani%20Hawaii%20neighborhood%20with%20tropical%20landscaping%2C%20palm%20trees%20and%20green%20lawns%2C%20distant%20view%20of%20the%20Waianae%20Mountains%2C%20warm%20golden%20afternoon%20light%2C%20well-maintained%20homes%20with%20warm%20tropical%20colors%2C%20professional%20travel%20photography%2C%20clean%20simple%20background%20with%20blue%20sky%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=mililani-hero-01&orientation=landscape",
  heroImageAlt: "Mililani Hawaii suburban neighborhood with tropical landscaping and mountain views",
  geoPosition: "21.4513;-158.0153",
  geoPlacename: "Mililani, Hawaii",
  regionalAgency: {
    name: "Honolulu Passport Agency",
    address: "300 Ala Moana Blvd, Room 1-224, Honolulu, HI 96850",
    distance: "About 18 miles south in downtown Honolulu",
  },
  nearbyCitiesZips: [
    { name: "Mililani", zips: "96789" },
    { name: "Wahiawa", zips: "96786" },
    { name: "Waipio", zips: "96797" },
    { name: "Waipahu", zips: "96797" },
    { name: "Pearl City", zips: "96782" },
    { name: "Aiea", zips: "96701" },
    { name: "Waimalu", zips: "96701" },
    { name: "Kapolei", zips: "96707" },
    { name: "Ewa Beach", zips: "96706" },
    { name: "Honolulu", zips: "96815" },
    { name: "Waikiki", zips: "96815" },
    { name: "Kailua", zips: "96734" },
    { name: "Kaneohe", zips: "96744" },
    { name: "Schofield Barracks", zips: "96857" },
    { name: "Wheeler AAF", zips: "96854" },
    { name: "Kunia", zips: "96759" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from your Mililani home in minutes." },
    { icon: "ri-truck-line", title: "Ship Your Documents", desc: "Use overnight shipping from Mililani or Waipahu to send your documents directly to us." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed before submission to reduce errors and delays." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots — no public appointment needed for Central Oahu residents." },
  ],
  testimonials: [
    {
      name: "David M.",
      location: "Mililani, HI",
      rating: 5,
      text: "I had a business trip to Seoul in 5 days and my passport was expired. Fast Passport Center processed my renewal from Mililani in 4 days. No need to drive through Honolulu traffic for an appointment.",
      service: "Passport Renewal",
    },
    {
      name: "Susan T.",
      location: "Wahiawa, HI",
      rating: 5,
      text: "Our family trip to the Philippines was in 2 weeks and my son's passport was nowhere to be found. They walked us through the replacement process from Wahiawa and had a new passport with time to spare.",
      service: "Lost Passport Replacement",
    },
    {
      name: "Gregory K.",
      location: "Waipio, HI",
      rating: 5,
      text: "Emergency trip to Japan for a family matter. I'm in Waipio near Mililani and FPC handled everything remotely. The passport arrived in 5 days. Their team was incredibly responsive.",
      service: "Expedited Passport",
    },
  ],
  facilities: [
    {
      id: "mililani-usps",
      name: "Mililani Post Office",
      type: "USPS Passport Acceptance",
      address: "95-1240 Meheula Pkwy",
      city: "Mililani",
      state: "HI",
      zip: "96789",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Mililani Town Center",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Appointment required. Call ahead to confirm passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "wahiawa-usps",
      name: "Wahiawa Post Office",
      type: "USPS Passport Acceptance",
      address: "156 N Kamehameha Hwy",
      city: "Wahiawa",
      state: "HI",
      zip: "96786",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "About 5 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Wahiawa area option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "waipahu-usps-mililani",
      name: "Waipahu Post Office",
      type: "USPS Passport Acceptance",
      address: "94-260 Farrington Hwy",
      city: "Waipahu",
      state: "HI",
      zip: "96797",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "About 8 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Waipahu area option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "pearl-city-usps-mililani",
      name: "Pearl City Post Office",
      type: "USPS Passport Acceptance",
      address: "550 Kamehameha Hwy",
      city: "Pearl City",
      state: "HI",
      zip: "96782",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "About 10 miles southeast",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Pearl City area option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in Mililani, HI?",
      a: "Through Fast Passport Center, expedited processing can be as fast as 24 hours. Standard processing through a Mililani or Wahiawa post office can take 2–6 months. For Central Oahu residents, our courier service is the fastest option without driving to Honolulu.",
    },
    {
      q: "Do I need to drive to Honolulu for a passport?",
      a: "No. Most Mililani and Central Oahu travelers start online and ship documents via overnight courier. The Mililani, Wahiawa, or Waipahu Post Offices can serve as acceptance facilities if needed, but you don't need to visit the Honolulu agency.",
    },
    {
      q: "Where is the closest passport acceptance facility to Mililani?",
      a: "The Mililani Post Office at 95-1240 Meheula Pkwy accepts passport applications by appointment. Wahiawa Post Office and Waipahu Post Office are also nearby options for Central Oahu residents.",
    },
    {
      q: "Can military families at Schofield Barracks use this service?",
      a: "Absolutely. We frequently serve military families at Schofield Barracks, Wheeler AAF, and across Central Oahu. Military travel and PCS moves often have tight deadlines, and our expedited processing is designed for urgent situations.",
    },
    {
      q: "Can I get an expedited passport if I've never had one before?",
      a: "Yes, but first-time applicants require an in-person acceptance clerk visit. The Mililani or Wahiawa Post Office are the most convenient options for Central Oahu residents. We guide you through exactly what to bring.",
    },
    {
      q: "How does shipping from Mililani work?",
      a: "You can ship overnight using FedEx or UPS from Mililani or Waipahu. Most Central Oahu residents use the FedEx location near Mililani Town Center or the UPS Store in Waipahu.",
    },
    {
      q: "Is Fast Passport Center an official government agency?",
      a: "No — Fast Passport Center is a private, approved and registered U.S. Department of State passport courier service. We work within the official passport system to provide expedited processing.",
    },
    {
      q: "What if I need a child passport in Mililani?",
      a: "Child passports (for minors 15 and under) require parental participation and an in-person acceptance step — both parents typically must consent. We provide clear instructions for Mililani and Wahiawa families.",
    },
    {
      q: "Can I get a passport if mine is lost or stolen?",
      a: "Yes. Lost or stolen passport replacements often require additional forms (including Form DS-64). We guide Central Oahu residents through the exact process, including the acceptance clerk visit and expedited submission.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Mililani", address: "95-1240 Meheula Pkwy, Mililani, HI 96789", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Waipahu", address: "94-229 Waipahu Depot St, Waipahu, HI 96797", hours: "Daily 7 AM – 11 PM" },
    { name: "CVS Pharmacy — Mililani", address: "95-221 Kipapa Dr, Mililani, HI 96789", hours: "Daily 7 AM – 10 PM" },
    { name: "The UPS Store — Mililani", address: "95-221 Kipapa Dr, Mililani, HI 96789", hours: "Call for hours" },
    { name: "Walmart — Mililani", address: "95-550 Lanikuhana Ave, Mililani, HI 96789", hours: "Call for hours" },
  ],
  dmvLocations: [
    {
      name: "Mililani DMV — Mililani Satellite City Hall",
      address: "95-1000 Ukuwai St, Mililani, HI 96789",
      hours: "Mon–Fri, 7:45 AM – 4:00 PM",
      note: "Driver licenses, REAL ID, state IDs. Appointment recommended.",
      featured: true,
    },
    {
      name: "Wahiawa DMV — Wahiawa Satellite City Hall",
      address: "330 N Cane St, Wahiawa, HI 96786",
      hours: "Mon–Fri, 7:45 AM – 4:00 PM",
      note: "Wahiawa area option. Appointment recommended.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "HNL",
      name: "Daniel K. Inouye International Airport",
      distance: "About 18 miles south — Honolulu",
      address: "300 Rodgers Blvd, Honolulu, HI 96819",
      note: "Major international hub for Hawaii. Most Central Oahu travelers fly from HNL for international trips.",
      flightUrl: "https://www.hawaii.gov/hnl/",
      flightLabel: "HNL Airport Website",
      featured: true,
    },
    {
      code: "OGG",
      name: "Kahului Airport (Maui)",
      distance: "Inter-island flights — Maui",
      address: "1 Keolani Pl, Kahului, HI 96732",
      note: "Inter-island connection option. Some Central Oahu residents connect through Maui for international travel.",
      flightUrl: "https://www.hawaii.gov/ogg/",
      flightLabel: "OGG Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from Mililani and Central Oahu.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Central Oahu travelers renew fast and avoid delays — no agency visit required for most renewals.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time applicants typically require an in-person acceptance clerk visit. We guide Central Oahu residents through every step.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Replacements often require additional forms. We guide you through exactly what's needed to avoid costly mistakes.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors (15 and under) require parental participation and an in-person acceptance step. We provide clear instructions for Mililani and Wahiawa families.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or a legal name change — we assist with corrected passports for Central Oahu residents.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-book-2-line",
      title: "Second Passport & Damaged",
      description: "Need a second passport for frequent travel, or replacing a damaged one? We handle both efficiently.",
      bullets: ["Frequent traveler solutions", "Damaged passport replacement", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Mililani and Central Oahu travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for Central Oahu residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance for Central Oahu residents with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for Central Oahu residents with lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Mililani and Wahiawa, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service for Central Oahu residents after marriage, divorce, or legal name changes." },
    { name: "Second Passport & Damaged", description: "Second passport issuance for frequent Central Oahu travelers and damaged passport replacement." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Government processing times change constantly. Many Central Oahu travelers assume 6–8 weeks is safe, only to find out it's now 3–4 months. Apply as soon as you book travel." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs so you get it right the first time." },
    { icon: "ri-file-damage-line", title: "Incomplete Forms", description: "One missing signature or wrong date and your application gets sent back. Our checklist catches errors before submission, saving you weeks of delay." },
    { icon: "ri-pen-nib-line", title: "Wrong Proof of Citizenship", description: "Not all birth certificates are accepted. Some require a long-form version. We tell you exactly which documents you need based on your situation." },
    { icon: "ri-calendar-close-line", title: "Missing Travel Proof for Emergency", description: "Regional agencies require proof of urgent travel. Without it, you're turned away. We help you prepare the right documentation before you need it." },
    { icon: "ri-map-pin-time-line", title: "Not Checking Name Consistency", description: "Your passport name must match your driver's license and ticket exactly. Middle names, suffixes, and hyphenated names cause more rejections than you'd think." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order. Takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist, instructions, and document review guidance." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using an overnight shipping option from Mililani or Waipahu." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency availability — so you don't have to drive to Honolulu." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport is completed within the timeframe you selected. No guessing, no hoping an appointment opens up." },
  ],
  processingOptions: [
    { speed: "2–9", unit: "Business Days", label: "Fast Passport Center", tag: "Most Popular", tagColor: "bg-[#F6C344] text-[#1A365D]", features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"], highlight: true },
    { speed: "3–5", unit: "Weeks", label: "Fast Passport Center", tag: "Standard Expedited", tagColor: "bg-[#1A365D]/10 text-[#1A365D]", features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"], highlight: false, smartSaver: true },
    { speed: "1–2", unit: "Months", label: "Self-Service (Post Office)", tag: "Slowest", tagColor: "bg-gray-100 text-gray-400", features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"], highlight: false, muted: true },
    { speed: "24", unit: "Hours*", label: "Regional Agency Processing", tag: "Emergency Only", tagColor: "bg-gray-100 text-gray-500", features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"], highlight: false, emergency: true },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited availability, often weeks out", fpc: "No appointment needed to start", fpcGood: true },
    { feature: "In-Person Visit", agency: "Required — downtown Honolulu location", fpc: "Most travelers ship documents overnight from Mililani", fpcGood: true },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options", fpcGood: true },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review to reduce delays", fpcGood: true },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission availability", fpcGood: true },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected", fpcGood: true },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only", fpc: "Most U.S. citizens with required documents", fpcGood: true },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Pearl City", route: "/pearl-city" },
    { name: "Honolulu", route: "/honolulu" },
    { name: "Kapolei", route: "/kapolei" },
    { name: "Kailua", route: "/kailua" },
    { name: "Hilo", route: "/hilo" },
    { name: "Kaneohe", route: "/kaneohe" },
  ],
  fpcOfficeAddress: "300 Ala Moana Blvd, Room 1-224, Honolulu, HI 96850",
  fpcOfficeNote: "After signing up online, choose what works best for you: courier pickup from your Mililani or Wahiawa home, ship with our prepaid label from any UPS/FedEx location in Mililani or Waipahu, or schedule a drop-off. Most Central Oahu clients ship overnight.",
  fpcOfficeDistance: "Serving Mililani and all of Central Oahu remotely",
  serviceAreaIntro: "Whether you are in Mililani, Wahiawa, Waipio, or anywhere in Central Oahu, Fast Passport Center makes expedited passport processing simple — no driving through Honolulu traffic, no agency lines.",
  nearbyCitiesIntro: "Anywhere in Central Oahu — start online, ship overnight from Mililani or Waipahu, and we handle the rest.",
  comparisonIntro: "",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Mililani as simple as possible.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Central Oahu travelers. Fast Passport Center reviews your documents before submission to catch these issues.",
  testimonialsIntro: "Real reviews from real travelers across Mililani, Wahiawa, and Waipio who trusted Fast Passport Center with their expedited passports.",
  faqIntro: "Everything Central Oahu travelers ask us most often about expedited passport services.",
  resourcesIntro: "",
  localTravelThemes: [],
  landmarks: [],
  island: "Oahu",
  islandNickname: "The Gathering Place",
  landmarkCards: [
    {
      name: "Mililani Town Planned Community",
      description: "One of the most well-designed planned communities in the U.S., Mililani features manicured neighborhoods, recreation centers, and a peaceful suburban lifestyle that attracts families with strong international travel connections.",
      imageUrl: "https://readdy.ai/api/search-image?query=Mililani%20Hawaii%20planned%20community%20neighborhood%2C%20well-maintained%20suburban%20streets%20with%20tropical%20landscaping%2C%20palm%20trees%20and%20green%20lawns%2C%20warm%20afternoon%20light%2C%20professional%20real%20estate%20photography%2C%20peaceful%20suburban%20atmosphere%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=mililani-community&orientation=landscape",
    },
    {
      name: "Wahiawa Botanical Garden",
      description: "A lush 27-acre garden in the Central Plateau, just minutes from Mililani. Wahiawa Botanical Garden showcases Hawaii's incredible native plant diversity and tropical beauty.",
      imageUrl: "https://readdy.ai/api/search-image?query=Wahiawa%20Botanical%20Garden%20Hawaii%2C%20lush%20tropical%20garden%20with%20exotic%20plants%20and%20trees%2C%20winding%20garden%20paths%2C%20dappled%20sunlight%20through%20canopy%2C%20professional%20botanical%20photography%2C%20vibrant%20greens%20and%20tropical%20colors%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=mililani-botgarden&orientation=landscape",
    },
    {
      name: "Schofield Barracks",
      description: "One of the largest U.S. Army installations in the world, Schofield Barracks neighbors Mililani and Wahiawa. Military families here frequently need expedited passports for overseas deployments and PCS moves.",
      imageUrl: "https://readdy.ai/api/search-image?query=Schofield%20Barracks%20Hawaii%2C%20large%20military%20installation%20surrounded%20by%20pineapple%20fields%2C%20Waianae%20mountains%20in%20background%2C%20sunny%20day%2C%20aerial%20view%2C%20professional%20military%20base%20photography%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=mililani-schofield&orientation=landscape",
    },
  ],
  travelTips: [
    { icon: "ri-shield-star-line", title: "Schofield Military Families", description: "Schofield Barracks and Wheeler AAF are adjacent to Mililani. PCS orders and TDY travel create urgent passport needs. We process military emergency passports with priority." },
    { icon: "ri-flight-takeoff-line", title: "HNL Is 18 Miles South", description: "Mililani residents take H-2 to H-1 to reach HNL in about 25 minutes. Check passport validity well before international trip booking." },
    { icon: "ri-truck-line", title: "Ship from Mililani Town Center", description: "FedEx and UPS at Mililani Town Center make overnight shipping straightforward. Ship documents before 6pm for next-morning processing." },
    { icon: "ri-global-line", title: "Diverse International Community", description: "Mililani has a highly educated, diverse community with strong Asia-Pacific travel ties. Japan, South Korea, and the Philippines are top destinations requiring current passports." },
    { icon: "ri-building-2-line", title: "Mililani City Hall Access", description: "The Mililani Satellite City Hall at 95-1000 Ukuwai St handles state IDs and REAL ID renewals — useful when updating ID documents for passport applications." },
    { icon: "ri-sun-line", title: "Central Oahu Lifestyle", description: "Mililani residents enjoy cooler temperatures than coastal Honolulu. The planned community lifestyle means frequent family travel — plan passport renewals in advance of school breaks." },
  ],
  statePageRoute: "/states/hawaii",
  statePageName: "Hawaii",
  dmvWebsite: "https://www.hawaii.gov/dot/highways/mvm",
  dmvLabel: "Hawaii DMV — Official Website",
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { icon: "ri-user-smile-line", label: "Birth & Death Certificates", desc: "Order certified Hawaii birth and death certificates from the Hawaii Department of Health Vital Records in Honolulu — online, by mail, or in person.", url: "https://health.hawaii.gov/vitalrecords/" },
    { icon: "ri-hearts-line", label: "Marriage Records", desc: "Request certified marriage certificates from the Hawaii Department of Health. Required for name change passport applications.", url: "https://health.hawaii.gov/vitalrecords/marriage/" },
    { icon: "ri-file-copy-line", label: "Divorce Decrees", desc: "Obtain certified divorce records through the Hawaii Family Court. Required for name change passport corrections.", url: "https://www.courts.state.hi.us/" },
    { icon: "ri-global-line", label: "VitalChek — Nationwide Records", desc: "If your record was issued outside Hawaii, order certified copies online through VitalChek for delivery in 5–10 business days.", url: "https://www.vitalchek.com/" },
  ],
};